'use client';

/**
 * AuthenticatedContainer ensures that only authenticated users can access the
 * components wrapped within it.
 *
 * For a detailed explanation of the different authentication patterns used
 * across the portals, see the central documentation here:
 * /docs/authentication-patterns.md
 */
import { PontCore } from '@/services/pontCore';
import { configureApiAuth } from '@verifime/api-definition';
import { usePasswordless, useTokens } from '@verifime/cognito-passwordless-auth';
import { LoadingScreen } from '@verifime/components';
import { responseInterceptor } from '@verifime/utils';
import { UserProvider } from '@verifime/views';
import { useRouter } from 'next/router';
import React from 'react';

// TODO: TECHNICAL DEBT (TD-016, see docs/technical-debt.md and #1816) - auth
// gating is divergent across the four portals, this list is matched two
// incompatible ways (startsWith for the redirect below vs exact includes for
// tokenRequired), and the person-api client config living in this component
// forces a two-tier public-page split. Hoist the client config out and converge
// the portals onto one mechanism.
const PATH_NOT_REQUIRE_AUTH = ['/sign-up', '/recover-invitation', '/token'];

export default function AuthenticatedContainer({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const { tokensParsed, signInStatus } = usePasswordless();
  const { getTokens, getTokensParsed } = useTokens();
  const router = useRouter();

  React.useEffect(() => {
    const validateTokensParsed = (tokensParsedToValidate: typeof tokensParsed) =>
      tokensParsedToValidate && new Date() < tokensParsedToValidate.expireAt;

    if (!validateTokensParsed(tokensParsed)) {
      getTokensParsed().then((newTokensParsed) => {
        if (!validateTokensParsed(newTokensParsed)) {
          const currentPath = router.pathname;
          // The home (login) page always no need auth, keep it as it is
          if ('/' === currentPath) {
            return;
          }

          if (!PATH_NOT_REQUIRE_AUTH.find((path) => currentPath.startsWith(path))) {
            router.push({
              pathname: '/',
              query: { from: encodeURIComponent(router.asPath) },
            });
          }
        }
      });
    }
  }, [getTokensParsed, router, tokensParsed]);

  PontCore.useFetch(async (url, options) => {
    const { accessToken: token } = (await getTokens()) || {};
    return await responseInterceptor(url, token, {
      ...options,
      tokenRequired: !PATH_NOT_REQUIRE_AUTH.includes(router.pathname),
    });
  });

  // Configure token getter synchronously during render so it is in place
  // before any child component effects fire (child effects run before parent effects).
  configureApiAuth(async () => getTokens().then((data) => data?.accessToken));

  // "/" and PATH_NOT_REQUIRE_AUTH (the customer onboarding flow) are public.
  // _app.tsx wraps them in AuthenticatedContainer too (they are not in
  // UNAUTHENTICATED_PATHS), and the redirect effect above already special-cases
  // them (no push for "/", startsWith-matched paths skip the push too). Mirror
  // that predicate here: only gate the render for the remaining, actually
  // protected paths. Never render THOSE until the session is confirmed signed
  // in - the redirect above is asynchronous, so rendering children
  // unconditionally would flash gated content at unauthenticated visitors
  // before it fires.
  const currentPath = router.pathname;
  const requiresAuth =
    currentPath !== '/' && !PATH_NOT_REQUIRE_AUTH.find((path) => currentPath.startsWith(path));
  // A usable session means SIGNED_IN, or REFRESHING_SIGN_IN (an established
  // session silently refreshing an expired token). Keep the page mounted for
  // both, so a background refresh does not tear down in-progress page state.
  // Every other status (CHECKING, SIGNING_IN, NOT_SIGNED_IN, SIGNING_OUT) means
  // no usable session yet, so show the loader instead of flashing gated content.
  const hasSession = signInStatus === 'SIGNED_IN' || signInStatus === 'REFRESHING_SIGN_IN';
  if (requiresAuth && !hasSession) {
    return (
      <UserProvider tokens={tokensParsed}>
        <LoadingScreen data-testid="auth-gate-loading" />
      </UserProvider>
    );
  }

  return <UserProvider tokens={tokensParsed}>{children}</UserProvider>;
}
